'use client';

// Inspired by react-hot-toast library

import * as React from 'react';

import type { ToastActionElement, ToastProps } from './toast';
import { ToastVariantType } from './toastType';

const TOAST_LIMIT = 3;
const TOAST_REMOVE_DELAY = 1000;
// 고정 토스트 퇴장 후 새 토스트를 추가할 때까지의 지연. toast-out(tailwind.config.ts)의
// 접힘이 80%(320ms) 시점에 끝나므로, 그 이후여야 새 토스트가 접힘에 끌려 내려가지 않는다
const TOAST_EXIT_ANIMATION_DURATION = 350;

export type ToasterToast = ToastProps & {
  id: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: ToastActionElement;
  button?: React.ReactNode;
  icon?: React.ReactNode;
  autoClose?: boolean;
  variant: ToastVariantType;
  onClick?: () => void;
  onClose?: () => void;
};

const actionTypes = {
  ADD_TOAST: 'ADD_TOAST',
  UPDATE_TOAST: 'UPDATE_TOAST',
  DISMISS_TOAST: 'DISMISS_TOAST',
  REMOVE_TOAST: 'REMOVE_TOAST',
  CLEAR_TOAST: 'CLEAR_TOAST'
} as const;

let count = 0;

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return count.toString();
}

type ActionType = typeof actionTypes;

type Action =
  | {
      type: ActionType['ADD_TOAST'];
      toast: ToasterToast;
    }
  | {
      type: ActionType['UPDATE_TOAST'];
      toast: Partial<ToasterToast>;
    }
  | {
      type: ActionType['DISMISS_TOAST'];
      toast?: Partial<ToasterToast>;
    }
  | {
      type: ActionType['REMOVE_TOAST'];
      toastId?: ToasterToast['id'];
    };

interface State {
  toasts: ToasterToast[];
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

const addToRemoveQueue = (toastId: string, onClose?: () => void) => {
  if (toastTimeouts.has(toastId)) {
    return;
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({
      type: 'REMOVE_TOAST',
      toastId: toastId
    });
  }, TOAST_REMOVE_DELAY);
  onClose?.();

  toastTimeouts.set(toastId, timeout);
};

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'ADD_TOAST': {
      const toasts = [action.toast, ...state.toasts];
      const openToasts = [action.toast, ...state.toasts.filter((t) => t.open)];
      const excess = openToasts.length - TOAST_LIMIT;

      if (excess <= 0) {
        return { ...state, toasts };
      }

      // 오래된 토스트부터 정리하되, autoClose가 꺼진 토스트는 유지
      const oldestFirst = openToasts.filter((t) => t.id !== action.toast.id).reverse();
      const evictTargets = [...oldestFirst.filter((t) => t.autoClose !== false), ...oldestFirst.filter((t) => t.autoClose === false)].slice(0, excess);
      const evictedIds = new Set(evictTargets.map((t) => t.id));

      evictTargets.forEach((t) => addToRemoveQueue(t.id, t.onClose));

      return {
        ...state,
        toasts: toasts.map((t) => (evictedIds.has(t.id) ? { ...t, open: false } : t))
      };
    }

    case 'UPDATE_TOAST':
      return {
        ...state,
        toasts: state.toasts.map((t) => (t.id === action.toast.id ? { ...t, ...action.toast } : t))
      };

    case 'DISMISS_TOAST': {
      const { toast } = action;
      const { id, onClose } = toast || {};

      // ! Side effects ! - This could be extracted into a dismissToast() action,
      // but I'll keep it here for simplicity
      if (id) {
        addToRemoveQueue(id, onClose ?? state.toasts.find((t) => t.id === id)?.onClose);
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id, toast.onClose);
        });
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === id || id === undefined
            ? {
                ...t,
                open: false
              }
            : t
        )
      };
    }
    case 'REMOVE_TOAST':
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: []
        };
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId)
      };
  }
};

const listeners: Array<(state: State) => void> = [];

let memoryState: State = { toasts: [] };

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach((listener) => {
    listener(memoryState);
  });
}

export type ToastType = Omit<ToasterToast, 'id'> & {
  id?: string;
};

function toast({ onClose, id: customId, ...props }: ToastType) {
  const id = customId ?? genId();

  const update = (props: ToasterToast) =>
    dispatch({
      type: 'UPDATE_TOAST',
      toast: { ...props, id }
    });
  const dismiss = () => {
    dispatch({
      type: 'DISMISS_TOAST',
      toast: {
        id,
        onClose
      }
    });
  };

  const addToast = () =>
    dispatch({
      type: 'ADD_TOAST',
      toast: {
        ...props,
        id,
        open: true,
        onClose,
        onOpenChange: (open) => {
          if (!open) dismiss();
        }
      }
    });

  if (customId) {
    const existing = memoryState.toasts.find((t) => t.id === customId);

    if (existing?.open) {
      return { id, dismiss, update };
    }
    if (existing) {
      const timeout = toastTimeouts.get(customId);
      if (timeout) {
        clearTimeout(timeout);
        toastTimeouts.delete(customId);
      }
      dispatch({ type: 'REMOVE_TOAST', toastId: customId });
    }
  }

  const openToasts = memoryState.toasts.filter((t) => t.open);
  const isFullWithPinnedOnly = openToasts.length >= TOAST_LIMIT && openToasts.every((t) => t.autoClose === false);
  const oldestPinned = isFullWithPinnedOnly && props.autoClose !== false ? openToasts[openToasts.length - 1] : undefined;

  if (oldestPinned) {
    dispatch({ type: 'DISMISS_TOAST', toast: { id: oldestPinned.id, onClose: oldestPinned.onClose } });
    setTimeout(addToast, TOAST_EXIT_ANIMATION_DURATION);
  } else {
    addToast();
  }

  // Radix의 pause/resume 고착 대비 안전장치: 내부 상태와 무관하게 일정 시간 뒤 무조건 닫는다.
  if (props.autoClose !== false) {
    setTimeout(dismiss, (props.duration ?? 3000) + 500);
  }

  return {
    id: id,
    dismiss,
    update
  };
}

function useToast() {
  const [state, setState] = React.useState<State>(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, [state]);

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: 'DISMISS_TOAST', toast: { id: toastId } }),
    clearToast: () => dispatch({ type: 'REMOVE_TOAST' })
  };
}

export { useToast, toast };
